import S, { DataSignal } from 's-js';
import SArray, { SArray as ArraySignal, lift } from 's-array';
import * as Promise from 'bluebird';
import TZDate from 'lib/TZDate';
import { TZUtils } from 'lib/tzutils';
import { CachedModelLoader } from 'lib/CachedModelLoader';
import { IAjax, Client } from 'lib/Ajax';
import { DraftObject } from 'lib/draftSignals';
import { AutoSaver } from 'lib/autoSave';
import uniqueIndex, { UniqueIndex } from 'lib/uniqueIndex';
import { Entrypoints } from 'core/app/entrypoints';

import { PCCache } from 'data/PCCache';

import { Berth } from 'data/models/Berth';
import { Comment, CommentOptions } from 'data/models/Comment';
import { DomainSummary } from 'data/models/DomainSummary';
import { NewTopicCardRequest } from 'data/models/NewTopicCardRequest';
import { PCUser } from 'data/models/PCUser';
import { TopicCard, TopicCardOptions } from 'data/models/TopicCard';
import { TopicCardAvailableAttachment } from 'data/models/TopicCardAvailableAttachment';
import { TopicCardChange } from 'data/models/TopicCardChange';
import { TopicCardOption } from 'data/models/TopicCardOption';
import { TopicCardQuestionType } from 'server/enums/TopicCardQuestionType';
import { TopicCardsResponse, TopicCardsResponseOptions, TopicCardViewModel, ApplicableTopicCardTemplate, ApplicableTopicCardTemplateOptions } from 'data/models/TopicCardsResponse';
import { TopicCardSelectedOption } from 'data/models/TopicCardSelectedOption';
import { TopicCardTemplate } from 'data/models/TopicCardTemplate';
import { TopicCardTemplateAttachment } from 'data/models/TopicCardTemplateAttachment';
import { TopicCardTemplateOption } from 'data/models/TopicCardTemplateOption';
import { TopicCardTemplateUserAssignment } from 'data/models/TopicCardTemplateUserAssignment';
import { TopicCardUserAssignment } from 'data/models/TopicCardUserAssignment';
import { TopicCardViewModelOptions } from 'data/models/TopicCardsResponse';
import { UploadFileResponseData } from 'data/models/UploadFileResponse';
import { UserSummary } from 'data/models/UserSummary';

import { LocalizationContext } from 'common/models/LocalizationContext';
import { TopicCardsCache } from './cache';
import { PersonProfilePopup, PersonProfilePopupParams } from 'portcall/personprofilepopup/model';
import { TopicCardListItem, TopicCardListItemParams } from 'portcall/topiccardlistitem/model';
import { UploadFileParams, UploadFile } from 'portcall/uploadfile/model';

export interface TopicCardsParams {
    id : number;
    selectedCard : CardKey;
}

type CardKey = { CardId : number, TemplateId? : undefined, BerthId? : undefined } | { CardId? : undefined, TemplateId : number, BerthId : number | null };

function IsAnsweredCardKey(key : CardKey) : key is { CardId : number } {
    return key.CardId !== undefined;
}

export function TopicCards(
    loader : CachedModelLoader<TopicCardsResponse, TopicCardsResponseOptions>,
    entrypoints : Entrypoints,
    ajax : IAjax,
    data : PCCache,
    cache : TopicCardsCache,
    domain : DomainSummary,
    tzutils : TZUtils,
    localization : LocalizationContext,
    uploadFileModel : (params : UploadFileParams<UploadFileResponseData>) => UploadFile,
    profilePopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup,
    topicCardListItemModel : (params : TopicCardListItemParams) => TopicCardListItem,
    { id, selectedCard: _cardKey } : TopicCardsParams
) {
    const { model } = loader({ Id: id }, 30),
        portcall = data.PortCalls.update1(null, { Id: id }),
        addedCardIds = new Set<number>(),
        removedTemplateIds = new Set<string>(),
        serverTopicCardsIdx = uniqueIndex(model.TopicCards, c => c.Id()),
        clock = tzutils.clock(1000), // Clock for time ago text on answered card items and card logs
        cardParams = SArray<CardOrTemplateParams>([]),
        newTopicCardUrl = entrypoints.NewTopicCard({ Id: id }),
        selectedCardKey = S.data<CardKey>(_cardKey),
        cards = cardParams
            .map(params => TopicCardOrTemplate(
                params,
                ajax,
                data,
                cache,
                clock,
                newTopicCardUrl,
                serverTopicCardsIdx,
                addedCardIds,
                removedTemplateIds,
                selectedCardKey,
                uploadFileModel,
                profilePopupModel,
                topicCardListItemModel
            ))
            .sort((c1, c2) =>
                c1.Model.Title().toLowerCase() < c2.Model.Title().toLowerCase() ? -1
              : c1.Model.Title().toLowerCase() > c2.Model.Title().toLowerCase() ? 1
              : 0),
        answeredCards = cards.filter(c => c.IsAnswered()),
        defaultCards = cards.filter(c => !c.IsAnswered()),
        selectedCard = cards.find(c => {
            const sel = selectedCardKey();
            return IsAnsweredCardKey(sel) ?
                c.IsAnswered() && c.Model.Id() === sel.CardId
              : !c.IsAnswered() && c.Template!.TemplateId() === sel.TemplateId && c.Template!.BerthId() === sel.BerthId;
        }),
        showActionsMenu = S.data(false);

    portcall.TopicCards.forEach(card => {
        // Discover a new TopicCard...
        // If the user just answered this card, do nothing (we already have a TopicCardOrTemplate object for it).
        // Otherwise, add it.
        if (!addedCardIds.has(card.Id())) cardParams.push({ card });
    }, card => {
        // Don't expect TopicCards to ever get removed (they can't be deleted). Do nothing.
    });

    model.ApplicableTemplates.forEach(template => {
        // Discover a new ApplicableTopicCardTemplate...
        // Make a new TopicCardOrTemplate object for it.
        cardParams.push({ template });
    }, template => {
        // Removing an ApplicableTopicCardTemplate...
        // If the user just answered this template, do nothing (we want to keep the answered TopicCardOrTemplate object).
        // Otherwise, remove it.
        if (!removedTemplateIds.has(ApplicableTopicCardTemplate.key(template))) {
            const param = cardParams().find(p => p.template === template);
            if (param) cardParams.remove(param);
        }
    });

    return {
        Id: id,
        Model: model,
        PortCall: portcall,
        AnsweredCards: answeredCards,
        DefaultCards: defaultCards,
        SelectedCardKey: selectedCardKey,
        SelectedCard: selectedCard,
        Domain: domain,
        Localization: localization,
        ShowActionsMenu: showActionsMenu,
        ToggleActionsMenu,
        GetOrgId,
        Now: () => clock.now(),
        GoBack: () => window.history.back()
    };

    function ToggleActionsMenu() {
        showActionsMenu(!showActionsMenu());
    }

    function MenuAction(action : () => void) {
        return () => S.freeze(() => {
            action();
            showActionsMenu(false);
        });
    }

    function CanManageServices() {
        return model.CanManageServices();
    }

    function GetOrgId() {
        return model.GetOrgId();
    }
}
export type TopicCards = ReturnType<typeof TopicCards>;

type CardOrTemplateParams = { card : TopicCard, template? : undefined } | { card? : undefined, template : ApplicableTopicCardTemplate };

let cardOrTemplateReactKey = 0;

// A single model that can represent an answered TopicCard or an unanswered TopicCardTemplate
function TopicCardOrTemplate(
    { card: _card, template } : CardOrTemplateParams,
    ajax : IAjax,
    data : PCCache,
    cache : TopicCardsCache,
    clock : { now : () => TZDate },
    answerTemplateUrl : string,
    serverTopicCardsIdx : UniqueIndex<TopicCardViewModel>,
    addedCardIds : Set<number>,
    removedTemplateIds : Set<string>,
    selectedCardKey : DataSignal<CardKey>,
    uploadFileModel : (params : UploadFileParams<UploadFileResponseData>) => UploadFile,
    profilePopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup,
    topicCardListItemModel : (params : TopicCardListItemParams) => TopicCardListItem
) {
    const
        isAnswered = S.data(!!_card),
        card = _card || CardFromTemplate(template!, isAnswered),
        client = ajax.Url<TopicCard>(() => card.Url() || '', { useCache: true }),
        answerTemplateClient = ajax.Url<NewTopicCardRequest, TopicCardViewModelOptions>(answerTemplateUrl, {
            include: [r => r.Card, r => card.Comments]
        }),
        { object: draft, methods: draftMethods } = DraftObject(card, [
            'ConfirmedDate',
            'DeniedDate',
            'IsYes',
            'TimeEntry.Response'
        ]),
        serverCard = serverTopicCardsIdx(card.Id),
        canEdit = () =>
            // i.e. permission to edit answered cards and answer unanswered cards
            isAnswered() ?
                serverCard() ?
                    serverCard()!.CanEdit() :
                    false :
                template!.CanAnswer(),
        canComment = () =>
            // Anyone can comment on answered cards. Need answer permissions to comment on a template.
            isAnswered() || template!.CanAnswer(),
        // Can't confirm/deny unanswered cards
        canConfirm = () => serverCard() ? serverCard()!.CanConfirm() : false,
        canViewAttachments = () => 
            serverCard() ?
                serverCard()!.CanConfirm() || serverCard()!.CanEdit() :
                false,
        listItem = topicCardListItemModel({ draft, canConfirm, clock }),
        newCardRequest = new NewTopicCardRequest();

    let answerTemplateTask = null as Promise<any> | null;

    const pendingAnswer = () => isAnswered() ? Promise.resolve() : answerTemplateTask ? answerTemplateTask : null,
        log = TopicCardOrTemplateLog(card, ajax, AnswerTemplate, pendingAnswer, uploadFileModel, profilePopupModel),
        selectedOption = S(() => card.SelectedOption() && TopicCardSelectedOptionModel(card.SelectedOption()!, ajax, AnswerTemplate, pendingAnswer));

    if (template) {
        // Initialize the newCardRequest object
        S(() => {
            if (!isAnswered()) {
                if (selectedOption()) newCardRequest.SelectedTemplateOptionId(selectedOption()!.OptionId());
                else newCardRequest.SelectedTemplateOptionId(null);
            }
        });
        newCardRequest.Card(draft);
    }

    AutoSaver(draftMethods.onWrite, Save);

    return {
        ReactKey: cardOrTemplateReactKey++, // key for React keying purposes
        // key used when selecting this card from the card list
        SelectionKey: () : CardKey => isAnswered() ? { CardId : card.Id() } : { TemplateId : template!.TemplateId(), BerthId : template!.BerthId() },
        Model: draft,
        ListItem: listItem,
        IsAnswered: isAnswered,
        Template: template,
        SelectedOption: selectedOption,
        Options: CardOrTemplateChildren(card.Options, t => t.Options)
            .sort((o1, o2) => o1.DisplayOrder() - o2.DisplayOrder()),
        AvailableAttachments: CardOrTemplateChildren(card.AvailableAttachments, t => t.Attachments)
            .sort((a1, a2) => a1.DisplayOrder() - a2.DisplayOrder()),
        People: CardOrTemplateChildren(card.People, t => t.People)
            .filter(ua => !!ua.User() && ua.User()!.Active())
            .sort((ua1, ua2) =>
                ua1.User() ?
                    ua2.User() ?
                        ua1.User()!.LastName().toLowerCase() < ua2.User()!.LastName().toLowerCase() ?
                            -1 :
                            ua1.User()!.LastName().toLowerCase() > ua2.User()!.LastName().toLowerCase() ?
                                1 :
                                0 :
                        -1 : // sort unpopulated users last
                    ua2.User() ?
                        1 :
                        0
            )
            .map(ua => UserAssignmentModel(ua, profilePopupModel)),
        Log: log,
        CanEdit: canEdit,
        CanComment: canComment,
        CanConfirm: canConfirm,
        CanViewAttachments: canViewAttachments
    };

    function Save() {
        var checkpoint = draftMethods.checkpoint();
        return pendingAnswer() ?
            pendingAnswer()!.then(() => client.Put(draft)).then(() => {
                checkpoint.commit();
            })
          : AnswerTemplate().then(() => {
              checkpoint.revert();
          });
    }

    function AnswerTemplate() {
        const templateKey = ApplicableTopicCardTemplate.key(template!);
        let req = answerTemplateClient.Post(newCardRequest);
        return answerTemplateTask = req.then(json => S.freeze(() => {
            isAnswered(true);
            const ts = req.Date;
            data.TopicCards.update1(ts, json.Card!, card);
            cache.TopicCardViewModels.update1(ts, Object.assign({}, json, { Card: null }));
            cache.ApplicableTopicCardTemplates.update(ts, [], [templateKey]);
            // Set flags so we know not to add/remove TopicCardOrTemplate models for the new TopicCard and the old ApplicableTopicCardTemplate
            addedCardIds.add(json.Id);
            removedTemplateIds.add(templateKey);
            // Also need to keep the current card selected
            selectedCardKey({ CardId: json.Id });
        }));
    }

    function CardOrTemplateChildren<TCardChild, TTemplateChild>(cardCollection : () => TCardChild[], templateCollection : (t : TopicCardTemplate) => () => TTemplateChild[]) {
        return lift<TCardChild | TTemplateChild>(() => isAnswered() ? cardCollection() : template!.Template() ? templateCollection(template!.Template()!)() : []);
    }
}
export type TopicCardOrTemplate = ReturnType<typeof TopicCardOrTemplate>;

function TopicCardSelectedOptionModel(
    selectedOption : TopicCardSelectedOption,
    ajax : IAjax,
    answerTemplate : () => Promise<any>,
    pendingAnswer : () => Promise<any> | null
) {
    const { object: draft, methods: draftMethods } = DraftObject(selectedOption),
        client = ajax.Url<TopicCardSelectedOption>(() => selectedOption.Url() || '', { useCache: true });

    AutoSaver(draftMethods.onWrite, Save);

    return draft;

    function Save() {
        var checkpoint = draftMethods.checkpoint();
        return pendingAnswer() ?
            pendingAnswer()!.then(() => client.Put(draft)).then(() => {
                checkpoint.commit();
            })
          : answerTemplate().then(() => {
              checkpoint.revert();
          });
    }
}

function UserAssignmentModel(
    assignment : TopicCardUserAssignment | TopicCardTemplateUserAssignment,
    profilePopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup
) {
    const user = S(() => assignment.User() && UserModel(assignment.User()!, profilePopupModel));

    return {
        Model: assignment,
        User: user
    };
}
export type UserAssignmentModel = ReturnType<typeof UserAssignmentModel>;

// Comments and TopicCardChanges
function TopicCardOrTemplateLog(
    card : TopicCard,
    ajax : IAjax,
    answerTemplate : () => Promise<any>,
    pendingAnswer : () => Promise<any> | null,
    uploadFileModel : (params : UploadFileParams<UploadFileResponseData>) => UploadFile,
    profilePopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup
) {
    const newComment = S.data(new Comment()),
        uploadFile = uploadFileModel({
            onComplete: json => newComment().AttachmentUrl(json.Url)
        }),
        addCommentClient = ajax.Url<Comment, CommentOptions>(() => card.CommentsUrl() || '', { useCache: true }),
        comments = card.Comments.map(c => CommentModel(c, profilePopupModel)),
        changes = card.Changes.map(c => ChangeModel(c, profilePopupModel)),
        combinedLog = (comments as ArraySignal<CardLogEntry>)
            .concat(changes)
            .sort((e1, e2) =>
                e1.Model.Event() ? 
                    e2.Model.Event() ?
                        +e2.Model.Event()!.Date() - +e1.Model.Event()!.Date() :
                        -1 : // sort unpopulated events last
                    e2.Model.Event() ?
                        1 :
                        0
            );

    return {
        LogEntries: combinedLog,
        NewComment: newComment,
        UploadFile: uploadFile,
        AddComment
    };

    function AddComment() {
        const req = pendingAnswer() ? 
            pendingAnswer()!.then(() => addCommentClient.Post(newComment()))
          : AnswerTemplateWithComment();

        req.then(() => newComment(new Comment()));
    }

    function AnswerTemplateWithComment() {
        card.Comments([newComment()]);
        return answerTemplate();
    }
}

export type CardLogEntry = CommentModel | ChangeModel;

function CommentModel(
    comment : Comment,
    profilePopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup
) {
    const commenter = S(() => comment.Event()?.User() ? UserModel(comment.Event()!.User()!, profilePopupModel) : null);

    return {
        Model: comment,
        Commenter: commenter,
        Type: 'comment' as const
    };
}
export type CommentModel = ReturnType<typeof CommentModel>;

function ChangeModel(
    change : TopicCardChange,
    profilePopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup
) {
    const changer = S(() => change.Event() && change.Event()!.User() && UserModel(change.Event()!.User()!, profilePopupModel));

    return {
        Model: change,
        Changer: changer,
        Type: 'change' as const
    };
}
export type ChangeModel = ReturnType<typeof ChangeModel>;

function UserModel(
    user : PCUser,
    profilePopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup
) {
    const showProfile = S.data(false),
        profilePopup = S(() => showProfile() ? profilePopupModel({ id: user.Id(), onHidePopup: () => showProfile(false) }) : null);

    return {
        Model: user,
        ToggleProfile: () => showProfile(!showProfile()),
        ProfilePopup: profilePopup
    };
}
export type UserModel = ReturnType<typeof UserModel>;

function CardFromTemplate(template : ApplicableTopicCardTemplate, isAnswered : () => boolean) {
    const card = new TopicCard(S.sample(() =>
        template.Template() ? template.Template()!.ToAnswerData(template.Berth) : { Id : 0}));

    S(() => {
        // TH 12/06/2016 -- Don't need to update from template anymore once the user answers the card.
        if (isAnswered()) return;
        const t = template.Template();
        if (!t) return;

        // TH 12/08/2016 -- Our strategy is to do a shallow copy from the TopicCardTemplate to the
        // new TopicCard. So our view code will read TopicCard properties from the new card object.
        // But for child collections (Options, Attachments, People), we'll bind to the Template
        // collections until the card gets answered.
        TopicCard.update(card, t.ToAnswerData(template.Berth));

        S(() => {
            if (t.QuestionType() === TopicCardQuestionType.MultipleChoice) {
                const _options = t.Options().slice().sort((o1, o2) => o1.DisplayOrder() - o2.DisplayOrder());
                card.SelectedOption(new TopicCardSelectedOption({
                    Id: 0,
                    // TH 12/06/2016 -- Select the first option by default.
                    // This is really an abuse of our schema, b/c TopicCardSelectedOption.OptionId
                    // is supposed to be a FK to a TopicCardOption, not a TopicCardTemplateOption.
                    // We'll use the TopicCardTemplateOption Id to pass w/ the NewTopicCardRequest,
                    // then switch to correctly using the TopicCardOption Id once the card gets answered.
                    OptionId: _options.length > 0 ? _options[0].Id() : 0
                }));
            } else {
                card.SelectedOption(null);
            }
        });
    });

    return card;
}


// WEBPACK FOOTER //
// ./apps/portcall/topiccards/model.ts